import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ATELIER, FONTS, ICON } from '../tokens';
import { useWorkspace } from '../WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import mainLogo from '../../assets/DAGGPT-01.png';

/**
 * ProjectsSidebar - premium left rail (288px).
 *
 * Top: real DAGGPT logo mark + "DAGGPT" wordmark (+ BETA chip)
 * Workspace card (avatar, balance, progress, Upgrade)
 * Workspace nav (Home / Library / Anchor)
 * Projects list with 3-dot row menu (rename / share / delete)
 */
export function ProjectsSidebar() {
  const navigate = useNavigate();
  const {
    projects, activeProjectId,
    conversation, conversations,
    createProject, openProject, togglePinProject,
    renameProject, deleteProject, deleteConversation,
    goHome, activeMode, switchMode,
  } = useWorkspace();
  const { userProfile, user, dgccBalance, logout } = useAuth() || {};

  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  // dialog = { kind: 'rename'|'share'|'delete'|'delete_thread', project, conversation }

  const allConvs = useMemo(
    () => [conversation, ...conversations].filter(Boolean),
    [conversation, conversations]
  );

  const convsByProject = useMemo(() => {
    const map = new Map();
    for (const c of allConvs) {
      if (!c.projectId) continue;
      if (!map.has(c.projectId)) map.set(c.projectId, []);
      map.get(c.projectId).push(c);
    }
    return map;
  }, [allConvs]);

  const filtered = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter(p => p.title.toLowerCase().includes(q));
  }, [projects, search]);

  const starred = filtered.filter(p => p.starred);
  const others  = filtered.filter(p => !p.starred);

  const name = userProfile?.name || user?.email || user?.user_metadata?.username || 'User';
  const initial = (name.charAt(0) || 'U').toUpperCase();

  const { refreshBalance } = useAuth();
  useEffect(() => {
    if (user?.id && !dgccBalance) {
      refreshBalance(user.id);
    }
  }, [user?.id, dgccBalance, refreshBalance]);

  return (
    <>
      <aside style={{
        width: 288,
        flexShrink: 0,
        height: '100dvh',
        background: ATELIER.bgSubtle,
        borderRight: `1px solid ${ATELIER.border}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundImage: `radial-gradient(rgba(124,58,237,0.04) 1px, transparent 1px)`,
        backgroundSize: '22px 22px',
      }}>
        {/* Brand - real logo + wordmark */}
        <div style={{
          height: 60,
          flexShrink: 0,
          padding: '0 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${ATELIER.borderSoft}`,
          position: 'relative',
          background: ATELIER.bgSubtle,
        }}>
          <div aria-hidden style={{
            position: 'absolute',
            top: -10, left: 4,
            width: 90, height: 90,
            background: 'radial-gradient(closest-side, rgba(167,139,250,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
            filter: 'blur(4px)',
          }} />
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex', alignItems: 'center', gap: 9,
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 0, height: 40, position: 'relative',
            }}
          >
            <img
              src={mainLogo}
              alt="DAGGPT"
              style={{
                height: 28,
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </button>
          <span style={{
            fontFamily: FONTS.mono,
            fontSize: 9, letterSpacing: '0.2em', fontWeight: 700,
            padding: '3px 7px', borderRadius: 999,
            background: ATELIER.accentSoft, color: ATELIER.accent,
            marginLeft: 'auto',
            position: 'relative',
          }}>BETA</span>
        </div>

        {/* Nav group */}
        <div style={{ padding: '8px 12px 4px', flexShrink: 0 }}>
          <SectionLabel>Workspace</SectionLabel>
          <NavItem
            active={!activeProjectId && activeMode !== 'slides'}
            onClick={() => goHome && goHome()}
            icon={(
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12L12 3l9 9" />
                <path d="M5 10v10h14V10" />
              </svg>
            )}
            label="Home"
          />
          <NavItem
            active={activeMode === 'slides'}
            onClick={() => {
              switchMode('slides');
              if (goHome) goHome(true);
            }}
            icon={(
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            )}
            label="Slides Studio"
          />
          <NavItem
            onClick={() => {}}
            icon={(
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={ICON.gallery} />
              </svg>
            )}
            label="Library"
            trailing={<ComingSoon />}
          />
          <NavItem
            onClick={() => {}}
            icon={(
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={ICON.history} />
              </svg>
            )}
            label="Anchor"
            trailing={<ComingSoon />}
          />
          <NavItem
            onClick={() => navigate('/dashboard')}
            icon={(
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            )}
            label="User Dashboard"
          />
          <div style={{ marginTop: 12, padding: '0 4px', display: 'flex', justifyContent: 'center' }}>
            <appkit-button />
          </div>
        </div>

        {/* Projects header */}
        <div style={{
          padding: '10px 12px 6px',
          display: 'flex', alignItems: 'center', gap: 8,
          flexShrink: 0,
        }}>
          <span style={{
            fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.24em',
            fontWeight: 700, color: ATELIER.inkMuted, textTransform: 'uppercase',
          }}>
            Projects · {projects.length}
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => createProject('Untitled project')}
            title="New project"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'transparent', border: 'none',
              padding: '3px 7px', borderRadius: 5,
              fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.18em',
              fontWeight: 700, textTransform: 'uppercase',
              color: ATELIER.accent, cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = ATELIER.accentSoft}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            + New
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '2px 12px 8px', flexShrink: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            height: 32, padding: '0 11px',
            background: ATELIER.bg,
            border: `1px solid ${ATELIER.border}`,
            borderRadius: 8,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke={ATELIER.inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={ICON.search} />
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search projects"
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                fontFamily: 'inherit', fontSize: 12.5, color: ATELIER.ink,
                letterSpacing: '-0.005em',
              }}
            />
            <span style={{
              fontFamily: FONTS.mono, fontSize: 9, color: ATELIER.inkMuted,
              padding: '1px 5px', borderRadius: 3,
              background: ATELIER.bgHover, border: `1px solid ${ATELIER.border}`,
              fontWeight: 600,
            }}>⌘K</span>
          </div>
        </div>

        {/* Scroll body */}
        <div className="dgw-scroll" style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          padding: '0 8px 12px',
        }}>
          {projects.length === 0 && <SidebarEmpty onCreate={() => createProject('Untitled project')} />}

          {starred.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <SectionLabel sub>Starred</SectionLabel>
              <AnimatePresence initial={false}>
                {starred.map(p => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    active={p.id === activeProjectId}
                    conversations={convsByProject.get(p.id) || []}
                    onOpen={() => openProject(p.id)}
                    onTogglePin={() => togglePinProject(p.id)}
                    onRename={() => setDialog({ kind: 'rename', project: p })}
                    onShare={() => setDialog({ kind: 'share', project: p })}
                    onDelete={() => setDialog({ kind: 'delete', project: p })}
                    onDeleteThread={(conv) => setDialog({ kind: 'delete_thread', conversation: conv })}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {others.length > 0 && (
            <div style={{ marginTop: starred.length > 0 ? 10 : 4 }}>
              {starred.length > 0 && <SectionLabel sub>All</SectionLabel>}
              <AnimatePresence initial={false}>
                {others.map(p => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    active={p.id === activeProjectId}
                    conversations={convsByProject.get(p.id) || []}
                    onOpen={() => openProject(p.id)}
                    onTogglePin={() => togglePinProject(p.id)}
                    onRename={() => setDialog({ kind: 'rename', project: p })}
                    onShare={() => setDialog({ kind: 'share', project: p })}
                    onDelete={() => setDialog({ kind: 'delete', project: p })}
                    onDeleteThread={(conv) => setDialog({ kind: 'delete_thread', conversation: conv })}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Workspace card (Bottom) */}
        <div style={{ padding: '12px', flexShrink: 0, borderTop: `1px solid ${ATELIER.borderSoft}` }}>
          <WorkspaceCard
            name={name}
            initial={initial}
            dgccObj={dgccBalance}
            onSettings={() => navigate('/dashboard?tab=get-dgcc')}
            onLogout={logout}
          />
        </div>
      </aside>

      {/* Dialogs */}
      <AnimatePresence>
        {dialog?.kind === 'rename' && (
          <RenameDialog
            project={dialog.project}
            onClose={() => setDialog(null)}
            onSubmit={(newTitle) => {
              renameProject(dialog.project.id, newTitle);
              setDialog(null);
            }}
          />
        )}
        {dialog?.kind === 'share' && (
          <ShareDialog
            project={dialog.project}
            onClose={() => setDialog(null)}
          />
        )}
        {dialog?.kind === 'delete' && (
          <DeleteDialog
            project={dialog.project}
            onClose={() => setDialog(null)}
            onConfirm={() => {
              deleteProject(dialog.project.id);
              setDialog(null);
            }}
          />
        )}
        {dialog?.kind === 'delete_thread' && (
          <DeleteThreadDialog
            conversation={dialog.conversation}
            onClose={() => setDialog(null)}
            onConfirm={() => {
              deleteConversation(dialog.conversation.id);
              setDialog(null);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* ═ WORKSPACE CARD ═══════════════════════════════════════ */
function WorkspaceCard({ name, initial, dgccObj, onSettings, onLogout }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const balance = dgccObj?.balance ?? 0;
  const totalPurchased = dgccObj?.total_purchased ?? 0;
  const totalBonus = dgccObj?.total_bonus ?? 0;
  
  // Total accumulated = purchases + transfers (in purchased) + bonuses/grants
  const totalGot = (totalPurchased + totalBonus) || balance; 
  
  const pct = Math.max(4, Math.min(100, (balance / Math.max(totalGot, 1)) * 100));

  const handleLogout = async () => {
    localStorage.removeItem('daggpt_token');
    localStorage.removeItem('daggpt_user');
    localStorage.removeItem('daggpt_authenticated');
    if (onLogout) {
      onLogout().catch(() => {});
    }
    window.location.href = '/';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'relative',
        padding: 14,
        background: ATELIER.bg,
        border: `1px solid ${ATELIER.border}`,
        borderRadius: 14,
        boxShadow: '0 4px 14px -4px rgba(124,58,237,0.14), 0 1px 2px rgba(10,10,20,0.04)',
        cursor: 'pointer',
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div aria-hidden style={{
        position: 'absolute',
        top: -30, right: -30,
        width: 120, height: 120,
        background: 'radial-gradient(closest-side, rgba(167,139,250,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, position: 'relative' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <span style={{
            width: 32, height: 32, borderRadius: '50%',
            background: ATELIER.gradient,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 12.5,
            boxShadow: '0 2px 6px rgba(124,58,237,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
            fontFamily: FONTS.sans,
          }}>{initial}</span>
          <span aria-hidden style={{
            position: 'absolute', right: -1, bottom: -1,
            width: 10, height: 10, borderRadius: '50%',
            background: ATELIER.ok,
            border: `2px solid ${ATELIER.bg}`,
          }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: ATELIER.ink,
            letterSpacing: '-0.005em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{name}</div>
          <div style={{
            marginTop: 1,
            fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.18em',
            color: ATELIER.ok, fontWeight: 700, textTransform: 'uppercase',
          }}>Online</div>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ATELIER.inkMid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </motion.div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: 'auto', opacity: 1, marginTop: 12 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{
              fontFamily: FONTS.serif, fontSize: 22, fontWeight: 500,
              color: ATELIER.ink, letterSpacing: '-0.02em', lineHeight: 1,
            }}>{balance.toLocaleString()}</span>
            <span style={{
              fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.22em',
              color: ATELIER.inkMuted, fontWeight: 700, textTransform: 'uppercase',
            }}>DGCC</span>
          </div>
          <span style={{
            fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.14em',
            color: ATELIER.inkFaint, fontWeight: 600,
          }}>/ {totalGot.toLocaleString()}</span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: ATELIER.bgHover, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
            style={{ height: '100%', background: ATELIER.gradient, boxShadow: `0 0 8px ${ATELIER.accent}66` }}
          />
        </div>

        <button
          type="button"
          onClick={onSettings}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            width: '100%', marginTop: 10,
            padding: '7px 10px',
            background: ATELIER.bgInk, color: '#fff', border: 'none',
            borderRadius: 8,
            fontFamily: FONTS.sans, fontSize: 11.5, fontWeight: 600,
            letterSpacing: '-0.005em',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(10,10,20,0.18), inset 0 1px 0 rgba(255,255,255,0.08)',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = ATELIER.accent}
          onMouseLeave={e => e.currentTarget.style.background = ATELIER.bgInk}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d={ICON.sparkle} />
          </svg>
          Top Up DGCC Credits
        </button>
        
        <button
          type="button"
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            width: '100%', marginTop: 8,
            padding: '7px 10px',
            background: 'transparent', color: ATELIER.inkMuted, border: `1px solid ${ATELIER.border}`,
            borderRadius: 8,
            fontFamily: FONTS.sans, fontSize: 11.5, fontWeight: 600,
            letterSpacing: '-0.005em',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
            e.currentTarget.style.color = '#ef4444';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = ATELIER.inkMuted;
            e.currentTarget.style.borderColor = ATELIER.border;
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Log Out
        </button>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═ NAV PRIMITIVES ═══════════════════════════════════════ */
function SectionLabel({ children, sub }) {
  return (
    <div style={{
      fontFamily: FONTS.mono,
      fontSize: sub ? 9.5 : 10, letterSpacing: '0.24em',
      fontWeight: 700, color: ATELIER.inkMuted,
      padding: sub ? '4px 10px 6px' : '4px 4px 6px',
      textTransform: 'uppercase',
    }}>
      {children}
    </div>
  );
}

function NavItem({ active, icon, label, trailing, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', height: 34, padding: '0 10px',
        background: active ? ATELIER.accentSoft : (hover ? ATELIER.bgHover : 'transparent'),
        color: active ? ATELIER.accent : ATELIER.inkMid,
        border: 'none', borderRadius: 9,
        cursor: 'pointer',
        fontFamily: FONTS.sans,
        fontSize: 12.5, fontWeight: active ? 600 : 500,
        letterSpacing: '-0.005em',
        textAlign: 'left',
        transition: 'background 0.12s, color 0.12s',
      }}
    >
      {active && (
        <span aria-hidden style={{
          position: 'absolute',
          left: -8, top: 8, bottom: 8, width: 3,
          background: ATELIER.accent,
          borderRadius: 2,
          boxShadow: `0 0 10px ${ATELIER.accent}`,
        }} />
      )}
      <span style={{
        width: 22, height: 22, borderRadius: 6,
        background: active ? ATELIER.bg : 'transparent',
        border: active ? `1px solid ${ATELIER.borderAccent}` : 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        color: active ? ATELIER.accent : ATELIER.inkSoft,
      }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {trailing}
    </button>
  );
}

function ComingSoon() {
  return (
    <span style={{
      fontFamily: FONTS.mono, fontSize: 8.5, letterSpacing: '0.18em',
      fontWeight: 600, color: ATELIER.inkFaint, textTransform: 'uppercase',
    }}>Soon</span>
  );
}

/* ═ PROJECT ROW + MENU ═══════════════════════════════════ */
function ProjectRow({ project, active, conversations, onOpen, onTogglePin, onRename, onShare, onDelete, onDeleteThread }) {
  const { conversation, loadConversation } = useWorkspace();
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const [expanded, setExpanded] = useState(active);

  useEffect(() => {
    if (active) {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  }, [active]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const totalConvs = conversations.length;
  const lastUpdated = Math.max(project.updatedAt || 0, ...conversations.map(c => c.updatedAt || 0));
  const subline = totalConvs === 0
    ? 'Empty'
    : `${totalConvs} ${totalConvs === 1 ? 'thread' : 'threads'} · ${relativeTime(lastUpdated)}`;

  const seed = hashStr(project.title || project.id || '');
  const pairs = [
    ['#7C3AED', '#4F46E5'], ['#5B21B6', '#7C3AED'], ['#6D28D9', '#A78BFA'],
    ['#4338CA', '#7C3AED'], ['#9333EA', '#5B21B6'], ['#A855F7', '#6366F1'],
    ['#6E3FE3', '#EC4899'], ['#4F46E5', '#9333EA'], ['#8B5CF6', '#3730A3'],
  ];
  const [a, b] = pairs[Math.abs(seed) % pairs.length];
  const angle = 110 + (seed % 60);
  const initial = (project.title || '?').trim().charAt(0).toUpperCase();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.18 }}
      style={{ position: 'relative' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', padding: '8px 10px',
          background: active ? ATELIER.accentSoft : (hover ? ATELIER.bgHover : 'transparent'),
          borderRadius: 10,
          position: 'relative',
          transition: 'background 0.12s',
        }}
      >
        {active && (
          <span aria-hidden style={{
            position: 'absolute', left: -8, top: 8, bottom: 8, width: 3,
            background: ATELIER.accent, borderRadius: 2,
            boxShadow: `0 0 8px ${ATELIER.accent}`,
          }} />
        )}

        <button
          type="button"
          onClick={() => {
            if (active) {
              setExpanded(prev => !prev);
            } else {
              onOpen();
            }
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            flex: 1, minWidth: 0,
            background: 'transparent', border: 'none',
            padding: 0, cursor: 'pointer', textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          {/* Rotating Chevron/Caret Icon */}
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
            stroke={active ? ATELIER.accent : ATELIER.inkSoft} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"
            style={{
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease',
              flexShrink: 0,
              marginLeft: 4,
            }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>

          <div style={{
            position: 'relative',
            width: 36, height: 36, borderRadius: 9,
            background: `linear-gradient(${angle}deg, ${a} 0%, ${b} 100%)`,
            overflow: 'hidden', flexShrink: 0,
            boxShadow: '0 2px 5px rgba(124,58,237,0.22)',
          }}>
            <div aria-hidden style={{
              position: 'absolute', left: 0, right: 0, top: 0, height: '55%',
              background: 'radial-gradient(ellipse 90% 80% at 30% 0%, rgba(255,255,255,0.42) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONTS.serif, fontSize: 17, fontWeight: 500,
              letterSpacing: '-0.03em', color: '#FFFFFF',
              textShadow: '0 1px 2px rgba(0,0,0,0.25)',
            }}>{initial}</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 600,
              color: active ? ATELIER.accent : ATELIER.ink,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              letterSpacing: '-0.005em',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              {project.starred && <StarGlyph size={10} filled />}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.title}</span>
            </div>
            <div style={{
              fontSize: 10, color: ATELIER.inkMuted, marginTop: 2,
              fontFamily: FONTS.mono, letterSpacing: '0.12em',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              textTransform: 'uppercase', fontWeight: 600,
            }}>{subline}</div>
          </div>
        </button>

        {/* 3-dot menu trigger */}
        <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
            title="More"
            style={{
              width: 22, height: 22, borderRadius: 5,
              background: menuOpen ? ATELIER.bgActive : 'transparent',
              border: 'none', cursor: 'pointer',
              display: (hover || menuOpen) ? 'inline-flex' : 'none',
              alignItems: 'center', justifyContent: 'center',
              color: ATELIER.inkSoft,
              transition: 'background 0.12s, color 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = ATELIER.bgActive; e.currentTarget.style.color = ATELIER.ink; }}
            onMouseLeave={e => { if (!menuOpen) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = ATELIER.inkSoft; } }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="1.6" />
              <circle cx="12" cy="12" r="1.6" />
              <circle cx="19" cy="12" r="1.6" />
            </svg>
          </button>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: -4 }}
                transition={{ duration: 0.14 }}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  minWidth: 168,
                  background: ATELIER.bg,
                  border: `1px solid ${ATELIER.border}`,
                  borderRadius: 10,
                  boxShadow: '0 16px 40px -10px rgba(10,10,20,0.18), 0 4px 10px -2px rgba(10,10,20,0.06)',
                  padding: 5,
                  zIndex: 50,
                }}
              >
                <MenuItem
                  onClick={() => { setMenuOpen(false); onTogglePin(); }}
                  iconPath={project.starred
                    ? 'M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z'
                    : 'M12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77z'}
                  filled={project.starred}
                  label={project.starred ? 'Unstar' : 'Star'}
                />
                <MenuItem
                  onClick={() => { setMenuOpen(false); onRename(); }}
                  iconPath="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                  label="Rename"
                />
                <MenuItem
                  onClick={() => { setMenuOpen(false); onShare(); }}
                  iconPath="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"
                  label="Share"
                />
                <div style={{ height: 1, margin: '4px 2px', background: ATELIER.borderSoft }} />
                <MenuItem
                  onClick={() => { setMenuOpen(false); onDelete(); }}
                  iconPath="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6"
                  label="Delete"
                  danger
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Nested Threads/Conversations inside this Project/Folder */}
      {expanded && conversations.length > 0 && (
        <div style={{
          paddingLeft: 46,
          paddingRight: 8,
          marginTop: 4,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {conversations
            .slice()
            .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
            .map(c => {
              const isThreadActive = c.id === conversation?.id;
              return (
                <ThreadRow
                  key={c.id}
                  conversation={c}
                  active={isThreadActive}
                  onSelect={() => loadConversation(c.id)}
                  onDelete={() => onDeleteThread(c)}
                />
              );
            })}
        </div>
      )}
    </motion.div>
  );
}

function ThreadRow({ conversation, active, onSelect, onDelete }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 30,
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={onSelect}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flex: 1,
          height: '100%',
          padding: '4px 8px',
          background: active ? ATELIER.bgHover : (hover ? 'rgba(124, 58, 237, 0.04)' : 'transparent'),
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          textAlign: 'left',
          color: active ? ATELIER.accent : ATELIER.inkMid,
          fontSize: 12,
          fontWeight: active ? 600 : 500,
          fontFamily: FONTS.sans,
          transition: 'all 0.12s ease',
          outline: 'none',
          minWidth: 0,
          paddingRight: 32,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" 
          stroke="currentColor" strokeWidth={active ? "2.2" : "1.8"} strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, color: active ? ATELIER.accent : ATELIER.inkSoft }}
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {conversation.turns?.length > 0 ? conversation.title : 'New thread'}
        </span>
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title="Delete thread"
        style={{
          position: 'absolute',
          right: 6,
          width: 22,
          height: 22,
          borderRadius: 6,
          background: hover ? 'rgba(239, 68, 68, 0.08)' : 'transparent',
          color: hover ? '#EF4444' : ATELIER.inkSoft,
          border: 'none',
          cursor: 'pointer',
          display: (hover || active) ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.12s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; e.currentTarget.style.color = '#EF4444'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = hover ? 'rgba(239, 68, 68, 0.08)' : 'transparent'; e.currentTarget.style.color = hover ? '#EF4444' : ATELIER.inkSoft; }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>
  );
}

function MenuItem({ iconPath, label, onClick, danger, filled }) {
  const [hover, setHover] = useState(false);
  const color = danger
    ? (hover ? ATELIER.error : ATELIER.error)
    : (hover ? ATELIER.accent : ATELIER.inkMid);
  const bg = hover
    ? (danger ? 'rgba(220,38,38,0.08)' : ATELIER.accentSoft)
    : 'transparent';
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', height: 32, padding: '0 9px',
        background: bg, color,
        border: 'none', borderRadius: 7,
        cursor: 'pointer',
        fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500,
        letterSpacing: '-0.005em',
        textAlign: 'left',
        transition: 'background 0.12s, color 0.12s',
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d={iconPath} />
      </svg>
      <span>{label}</span>
    </button>
  );
}

/* ═ DIALOG SHELL ═════════════════════════════════════════ */
function DialogShell({ onClose, children, maxWidth = 440 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,10,20,0.32)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        onClick={e => e.stopPropagation()}
        style={{
          background: ATELIER.bg,
          border: `1px solid ${ATELIER.border}`,
          borderRadius: 16,
          boxShadow: '0 30px 80px -20px rgba(10,10,20,0.35), 0 10px 24px -8px rgba(124,58,237,0.12)',
          width: '100%',
          maxWidth,
          padding: 24,
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

/* ═ RENAME DIALOG ════════════════════════════════════════ */
function RenameDialog({ project, onClose, onSubmit }) {
  const [value, setValue] = useState(project.title || '');
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, []);

  const canSubmit = value.trim().length > 0 && value.trim() !== project.title;

  return (
    <DialogShell onClose={onClose}>
      <DialogLabel>Rename project</DialogLabel>
      <h2 style={{
        fontFamily: FONTS.serif, fontSize: 24, fontWeight: 400,
        letterSpacing: '-0.025em', color: ATELIER.ink,
        margin: '6px 0 16px', lineHeight: 1.2,
      }}>
        Give it a new name.
      </h2>

      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && canSubmit) onSubmit(value.trim());
        }}
        placeholder="Project name"
        style={{
          width: '100%',
          padding: '12px 14px',
          background: ATELIER.bg,
          border: `1px solid ${ATELIER.borderStrong}`,
          borderRadius: 10,
          fontFamily: FONTS.sans, fontSize: 14,
          color: ATELIER.ink,
          outline: 'none',
          letterSpacing: '-0.005em',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
        onFocus={e => {
          e.currentTarget.style.borderColor = ATELIER.accent;
          e.currentTarget.style.boxShadow = ATELIER.shadowFocus;
        }}
        onBlur={e => {
          e.currentTarget.style.borderColor = ATELIER.borderStrong;
          e.currentTarget.style.boxShadow = 'none';
        }}
      />

      <DialogActions
        onCancel={onClose}
        primaryLabel="Save"
        onPrimary={() => onSubmit(value.trim())}
        primaryDisabled={!canSubmit}
      />
    </DialogShell>
  );
}

/* ═ SHARE DIALOG ═════════════════════════════════════════ */
function ShareDialog({ project, onClose }) {
  const [isPublic, setIsPublic] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/p/${project.id}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <DialogShell onClose={onClose}>
      <DialogLabel>Share project</DialogLabel>
      <h2 style={{
        fontFamily: FONTS.serif, fontSize: 24, fontWeight: 400,
        letterSpacing: '-0.025em', color: ATELIER.ink,
        margin: '6px 0 4px', lineHeight: 1.2,
      }}>
        {project.title}
      </h2>
      <p style={{
        fontSize: 13, color: ATELIER.inkSoft, lineHeight: 1.5,
        margin: '0 0 18px', letterSpacing: '-0.005em',
      }}>
        Choose who can see this project and copy a link to share it.
      </p>

      {/* Visibility toggle */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
        marginBottom: 14,
      }}>
        <VisibilityOption
          active={!isPublic}
          onClick={() => setIsPublic(false)}
          iconPath="M12 2a5 5 0 00-5 5v4H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2h-2V7a5 5 0 00-5-5z"
          title="Private"
          caption="Only you and invited members"
        />
        <VisibilityOption
          active={isPublic}
          onClick={() => setIsPublic(true)}
          iconPath="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20"
          title="Public"
          caption="Anyone with the link"
        />
      </div>

      {/* Link field */}
      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 8,
        marginTop: 4,
      }}>
        <div style={{
          flex: 1, minWidth: 0,
          padding: '10px 12px',
          background: ATELIER.bgSubtle,
          border: `1px solid ${ATELIER.border}`,
          borderRadius: 10,
          fontFamily: FONTS.mono, fontSize: 12,
          color: ATELIER.inkMid,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          letterSpacing: '-0.005em',
          display: 'flex', alignItems: 'center',
        }}>
          {shareUrl}
        </div>
        <button
          type="button"
          onClick={copy}
          style={{
            padding: '0 16px',
            background: copied ? ATELIER.ok : ATELIER.bgInk,
            color: '#fff', border: 'none',
            borderRadius: 10,
            fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600,
            letterSpacing: '-0.005em',
            cursor: 'pointer',
            minWidth: 96,
            transition: 'background 0.2s',
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>

      {isPublic && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            marginTop: 14, padding: '10px 12px',
            background: ATELIER.accentSoft,
            border: `1px solid ${ATELIER.borderAccent}`,
            borderRadius: 10,
            fontFamily: FONTS.sans, fontSize: 12,
            color: ATELIER.accentDeep, lineHeight: 1.5,
            letterSpacing: '-0.005em',
          }}>
          <strong>Public preview:</strong> anyone with the link will see the project's threads and outputs. Toggle back to Private anytime.
        </motion.div>
      )}

      <DialogActions
        onCancel={onClose}
        primaryLabel="Done"
        onPrimary={onClose}
      />
    </DialogShell>
  );
}

function VisibilityOption({ active, onClick, iconPath, title, caption }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
        padding: '12px 14px',
        background: active ? ATELIER.accentSoft : ATELIER.bg,
        border: `1px solid ${active ? ATELIER.borderAccent : ATELIER.border}`,
        borderRadius: 12,
        cursor: 'pointer',
        fontFamily: FONTS.sans, textAlign: 'left',
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke={active ? ATELIER.accent : ATELIER.inkMid}
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d={iconPath} />
        </svg>
        <span style={{
          fontSize: 13, fontWeight: 600,
          color: active ? ATELIER.accent : ATELIER.ink,
          letterSpacing: '-0.005em',
        }}>{title}</span>
        {active && (
          <span style={{ marginLeft: 'auto' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke={ATELIER.accent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
        )}
      </div>
      <span style={{
        fontSize: 11.5, color: ATELIER.inkSoft, lineHeight: 1.4,
        letterSpacing: '-0.005em',
      }}>{caption}</span>
    </button>
  );
}

/* ═ DELETE DIALOG ════════════════════════════════════════ */
function DeleteDialog({ project, onClose, onConfirm }) {
  const [typed, setTyped] = useState('');
  const confirmPhrase = 'delete';
  const canConfirm = typed.trim().toLowerCase() === confirmPhrase;

  return (
    <DialogShell onClose={onClose} maxWidth={460}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 40, height: 40, borderRadius: 10,
        background: 'rgba(220,38,38,0.10)',
        border: '1px solid rgba(220,38,38,0.22)',
        marginBottom: 14,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke={ATELIER.error} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>

      <DialogLabel danger>Delete project</DialogLabel>
      <h2 style={{
        fontFamily: FONTS.serif, fontSize: 24, fontWeight: 400,
        letterSpacing: '-0.025em', color: ATELIER.ink,
        margin: '6px 0 10px', lineHeight: 1.2,
      }}>
        Delete "{project.title}"?
      </h2>
      <p style={{
        fontSize: 13.5, color: ATELIER.inkMid, lineHeight: 1.55,
        margin: '0 0 14px', letterSpacing: '-0.005em',
      }}>
        This <strong>cannot be undone</strong>. All threads, outputs, and
        memory attached to this project will be permanently removed and
        cannot be recovered.
      </p>

      <label style={{
        display: 'block',
        fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.22em',
        fontWeight: 700, color: ATELIER.inkMuted,
        textTransform: 'uppercase', marginBottom: 6,
      }}>
        Type "{confirmPhrase}" to confirm
      </label>
      <input
        value={typed}
        onChange={e => setTyped(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && canConfirm) onConfirm(); }}
        autoFocus
        placeholder={confirmPhrase}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: ATELIER.bg,
          border: `1px solid ${canConfirm ? ATELIER.error : ATELIER.borderStrong}`,
          borderRadius: 10,
          fontFamily: FONTS.mono, fontSize: 13,
          color: ATELIER.ink,
          outline: 'none',
          letterSpacing: '0.05em',
          transition: 'border-color 0.15s',
        }}
      />

      <DialogActions
        onCancel={onClose}
        primaryLabel="Delete forever"
        onPrimary={onConfirm}
        primaryDisabled={!canConfirm}
        danger
      />
    </DialogShell>
  );
}

/* ═ DELETE THREAD DIALOG ═════════════════════════════════ */
function DeleteThreadDialog({ conversation, onClose, onConfirm }) {
  return (
    <DialogShell onClose={onClose} maxWidth={440}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 40, height: 40, borderRadius: 10,
        background: 'rgba(220,38,38,0.10)',
        border: '1px solid rgba(220,38,38,0.22)',
        marginBottom: 14,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke={ATELIER.error} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </div>

      <DialogLabel danger>Delete thread</DialogLabel>
      <h2 style={{
        fontFamily: FONTS.serif, fontSize: 22, fontWeight: 400,
        letterSpacing: '-0.02em', color: ATELIER.ink,
        margin: '6px 0 10px', lineHeight: 1.25,
      }}>
        Delete "{conversation?.title || 'Untitled thread'}"?
      </h2>
      <p style={{
        fontSize: 13.5, color: ATELIER.inkMid, lineHeight: 1.5,
        margin: '0 0 20px', letterSpacing: '-0.005em',
      }}>
        Are you sure you want to delete this conversation thread? This <strong>cannot be undone</strong> and all chat turns, uploaded context, and outputs inside this thread will be permanently erased.
      </p>

      <DialogActions
        onCancel={onClose}
        primaryLabel="Delete thread"
        onPrimary={onConfirm}
        danger
      />
    </DialogShell>
  );
}

/* ═ DIALOG HELPERS ═══════════════════════════════════════ */
function DialogLabel({ children, danger }) {
  return (
    <div style={{
      fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.24em',
      fontWeight: 700, color: danger ? ATELIER.error : ATELIER.accent,
      textTransform: 'uppercase',
    }}>{children}</div>
  );
}

function DialogActions({ onCancel, primaryLabel, onPrimary, primaryDisabled, danger }) {
  return (
    <div style={{
      display: 'flex', gap: 8, justifyContent: 'flex-end',
      marginTop: 20, paddingTop: 16,
      borderTop: `1px solid ${ATELIER.borderSoft}`,
    }}>
      <button
        type="button"
        onClick={onCancel}
        style={{
          padding: '9px 14px',
          background: 'transparent',
          color: ATELIER.inkMid,
          border: `1px solid ${ATELIER.border}`,
          borderRadius: 9,
          fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600,
          cursor: 'pointer',
          letterSpacing: '-0.005em',
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = ATELIER.bgHover; e.currentTarget.style.borderColor = ATELIER.borderStrong; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = ATELIER.border; }}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onPrimary}
        disabled={primaryDisabled}
        style={{
          padding: '9px 16px',
          background: primaryDisabled
            ? ATELIER.bgHover
            : (danger ? ATELIER.error : ATELIER.bgInk),
          color: primaryDisabled ? ATELIER.inkMuted : '#fff',
          border: 'none',
          borderRadius: 9,
          fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600,
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
          letterSpacing: '-0.005em',
          boxShadow: primaryDisabled
            ? 'none'
            : (danger
                ? '0 2px 6px rgba(220,38,38,0.28), inset 0 1px 0 rgba(255,255,255,0.10)'
                : '0 2px 6px rgba(10,10,20,0.18), inset 0 1px 0 rgba(255,255,255,0.08)'),
          transition: 'background 0.15s',
        }}
      >
        {primaryLabel}
      </button>
    </div>
  );
}

/* ═ EMPTY + STAR + HELPERS ═══════════════════════════════ */
function SidebarEmpty({ onCreate }) {
  return (
    <div style={{
      padding: '20px 14px',
      textAlign: 'center',
      color: ATELIER.inkSoft,
    }}>
      <div style={{
        fontFamily: FONTS.serif, fontSize: 15, fontStyle: 'italic',
        color: ATELIER.inkSoft, lineHeight: 1.3, letterSpacing: '-0.015em',
      }}>
        No projects yet.
      </div>
      <div style={{
        marginTop: 6,
        fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.22em',
        color: ATELIER.inkMuted, fontWeight: 600, textTransform: 'uppercase',
      }}>
        Create one to get started
      </div>
      <button
        type="button"
        onClick={onCreate}
        style={{
          marginTop: 14,
          padding: '6px 12px',
          background: ATELIER.accent,
          color: '#fff', border: 'none',
          borderRadius: 7,
          fontFamily: FONTS.sans, fontSize: 11.5, fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        + New project
      </button>
    </div>
  );
}

function StarGlyph({ size = 12, filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function relativeTime(ts) {
  if (!ts) return 'Just now';
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return h;
}
